import { useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { useAppContext } from "@/App";
import api from "@/lib/api";
import { Bird, Mail, Loader2, ArrowRight, Zap, Shield } from "lucide-react";

export default function AuthPage() {
  const { setAuth } = useAppContext();
  const nav = useNavigate();
  const [searchParams] = useSearchParams();
  const inviteCode = searchParams.get("invite") || "";

  const [email, setEmail] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  const handleLogin = async (e) => {
    e.preventDefault();
    if (!email.includes("@")) {
      setError("Please enter a valid email");
      return;
    }
    setLoading(true);
    setError("");
    try {
      const magicRes = await api.requestMagicLink(email);
      const verifyRes = await api.verifyMagicLink(magicRes.token);
      const authData = {
        jwt: verifyRes.jwt,
        user: verifyRes.user,
      };
      setAuth(authData);
      localStorage.setItem("pigeon-auth", JSON.stringify(authData));

      if (inviteCode) {
        nav(`/join?invite=${inviteCode}`);
      } else {
        nav("/portal");
      }
    } catch (err) {
      setError(err?.response?.data?.detail || "Login failed");
    }
    setLoading(false);
  };

  return (
    <div className="min-h-[100dvh] bg-white flex flex-col relative overflow-hidden" data-testid="auth-page">
      <div className="absolute inset-0 z-0">
        <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_top,rgba(101,163,13,0.05),transparent_60%)]" />
        <div className="absolute bottom-0 left-0 right-0 h-1/2 bg-gradient-to-t from-white to-transparent" />
      </div>

      <header className="relative z-10 flex items-center justify-between px-6 py-5 border-b border-slate-200">
        <div className="flex items-center gap-3 cursor-pointer" onClick={() => nav("/landing")}>
          <div className="w-10 h-10 bg-lime-500 rounded-xl flex items-center justify-center shadow-md">
            <Bird className="w-5 h-5 text-slate-950" strokeWidth={2} />
          </div>
          <div>
            <h1 className="text-lg font-black tracking-tighter uppercase text-slate-900">Pigeon Supply</h1>
            <p className="text-[10px] font-bold tracking-widest uppercase text-slate-400">Supply Platform</p>
          </div>
        </div>
      </header>

      <main className="relative z-10 flex-1 flex items-center justify-center p-6">
        <div className="w-full max-w-md">
          <div className="text-center mb-10">
            <div className="w-16 h-16 bg-lime-50 border border-lime-300 rounded-xl flex items-center justify-center mx-auto mb-6">
              <Shield className="w-8 h-8 text-lime-500" />
            </div>
            <h2 className="text-3xl md:text-4xl font-black tracking-tighter uppercase text-slate-900 mb-3">
              {inviteCode ? "Join Hub" : "Command Access"}
            </h2>
            <p className="text-sm text-slate-400 font-medium">
              {inviteCode
                ? "Enter your email to join the hub"
                : "Enter your email to access your supply platform"}
            </p>
          </div>

          {inviteCode && (
            <div className="mb-6 p-3 bg-lime-50 border border-lime-300 rounded-xl flex items-center gap-3" data-testid="invite-badge">
              <Zap className="w-5 h-5 text-lime-600 shrink-0" />
              <div>
                <p className="text-xs font-bold text-lime-600 uppercase tracking-wider">Invite Code</p>
                <p className="text-sm text-slate-400 font-mono">{inviteCode}</p>
              </div>
            </div>
          )}

          <form onSubmit={handleLogin} className="space-y-4">
            <div>
              <label className="block text-xs font-bold text-slate-400 uppercase tracking-widest mb-2">Email Address</label>
              <div className="relative">
                <Mail className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-slate-400" />
                <input
                  data-testid="auth-email-input"
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder="you@company.com"
                  className="w-full pl-12 pr-4 py-4 bg-slate-50 border border-slate-200 rounded-xl text-slate-900 font-medium placeholder:text-slate-400 focus:border-lime-500 focus:ring-1 focus:ring-lime-500/20 outline-none transition-all"
                  autoFocus
                />
              </div>
            </div>

            {error && (
              <p className="text-red-400 text-sm font-medium" data-testid="auth-error">{error}</p>
            )}

            <button
              data-testid="auth-submit-btn"
              type="submit"
              disabled={loading || !email}
              className="w-full py-4 bg-lime-500 hover:bg-lime-400 disabled:bg-slate-100 disabled:text-slate-400 text-slate-950 rounded-xl text-sm font-black uppercase tracking-wider flex items-center justify-center gap-2 transition-all shadow-md hover:shadow-lg"
            >
              {loading ? (
                <Loader2 className="w-5 h-5 animate-spin" />
              ) : (
                <>
                  Continue
                  <ArrowRight className="w-4 h-4" />
                </>
              )}
            </button>
          </form>

          <p className="text-center text-[11px] text-slate-400 mt-6 font-medium">
            No password needed. We use secure magic links.
          </p>
        </div>
      </main>

      <footer className="relative z-10 text-center py-4 border-t border-slate-100">
        <p className="text-[10px] font-bold tracking-[0.2em] uppercase text-slate-400">Pigeon Supply v2.0 — Secure Access</p>
      </footer>
    </div>
  );
}
